/**
 * Types for API responses — verified against worker/routes/*.
 *
 * Role literals match db/schema.js users.role column values (all uppercase).
 */

export type UserRole =
  | "SUPERADMIN"
  | "ADMIN"
  | "FULL_ACCESS"
  | "EDIT_SHARE"
  | "EDIT";

/** Every role except SUPERADMIN — i.e. anyone who belongs to a workspace. */
export type WorkspaceRole = Exclude<UserRole, "SUPERADMIN">;

export const WORKSPACE_ROLES: WorkspaceRole[] = [
  "ADMIN",
  "FULL_ACCESS",
  "EDIT_SHARE",
  "EDIT",
];

/**
 * Mirrors worker/lib/permissions.js PERMISSION_MATRIX.CREATE_PROJECT.
 * Client-side check so we don't show a "New Project" button that will just
 * 403 — the backend is still the real gate, this is only for UI affordance.
 */
export function canCreateProject(role: string | null | undefined): boolean {
  return role === "ADMIN" || role === "FULL_ACCESS";
}

/** Mirrors PERMISSION_MATRIX.INVITE_USER — ADMIN only. */
export function canInviteUsers(role: string | null | undefined): boolean {
  return role === "ADMIN";
}

/**
 * Mirrors PERMISSION_MATRIX.DELETE_PROJECT (currently identical to
 * CREATE_PROJECT — both gate on the same two roles — but kept as its own
 * helper since the matrix could diverge later).
 */
export function canDeleteProject(role: string | null | undefined): boolean {
  return role === "ADMIN" || role === "FULL_ACCESS";
}

/** Mirrors PERMISSION_MATRIX.CREATE_SHARE_LINK — ADMIN/FULL_ACCESS/EDIT_SHARE, not EDIT. */
export function canCreateShareLink(role: string | null | undefined): boolean {
  return role === "ADMIN" || role === "FULL_ACCESS" || role === "EDIT_SHARE";
}

/** Mirrors PERMISSION_MATRIX.CREATE_FOLDER — all workspace tiers. */
export function canCreateFolder(role: string | null | undefined): boolean {
  return role === "ADMIN" || role === "FULL_ACCESS" || role === "EDIT_SHARE" || role === "EDIT";
}

/** Mirrors PERMISSION_MATRIX.DELETE_FOLDER — ADMIN + FULL_ACCESS (narrowed 2026-08-21; EDIT_SHARE dropped). */
export function canDeleteFolder(role: string | null | undefined): boolean {
  return role === "ADMIN" || role === "FULL_ACCESS";
}

/** Mirrors PERMISSION_MATRIX.MANAGE_FOLDER_ACCESS — ADMIN + FULL_ACCESS. */
export function canManageFolderAccess(role: string | null | undefined): boolean {
  return role === "ADMIN" || role === "FULL_ACCESS";
}

/** Mirrors PERMISSION_MATRIX.SHARE_FOLDER — ADMIN + FULL_ACCESS + EDIT_SHARE. */
export function canShareFolder(role: string | null | undefined): boolean {
  return role === "ADMIN" || role === "FULL_ACCESS" || role === "EDIT_SHARE";
}

/**
 * Member management — ADMIN + FULL_ACCESS (2026-08-21 role-parity pass).
 * Mirrors PERMISSION_MATRIX.VIEW_MEMBERS / INVITE_USER / REMOVE_USER / CHANGE_USER_ROLE.
 * FA = near-owner (Frame.io "Full Access"). The FA-cannot-remove/demote-the-ADMIN-owner
 * guard is target-dependent — enforced server-side + per-row in the Users UI, not here.
 */
export function canManageMembers(role: string | null | undefined): boolean {
  return role === "ADMIN" || role === "FULL_ACCESS";
}

/** Mirrors PERMISSION_MATRIX.VIEW_USAGE — ADMIN + FULL_ACCESS (2026-08-21). Usage + storage hidden from EDIT/EDIT_SHARE (Frame.io parity). */
export function canViewUsage(role: string | null | undefined): boolean {
  return role === "ADMIN" || role === "FULL_ACCESS";
}

/** Mirrors PERMISSION_MATRIX.DELETE_ANY_VIDEO — ADMIN + FULL_ACCESS (narrowed 2026-08-21). EDIT/EDIT_SHARE delete OWN uploads only. */
export function canDeleteAnyVideo(role: string | null | undefined): boolean {
  return role === "ADMIN" || role === "FULL_ACCESS";
}

/** Roles a workspace admin can invite (admin itself is provisioned by superadmin). */
export const INVITABLE_ROLES = ["FULL_ACCESS", "EDIT_SHARE", "EDIT"] as const;
export type InvitableRole = (typeof INVITABLE_ROLES)[number];

export const ROLE_DISPLAY_NAMES: Record<UserRole, string> = {
  SUPERADMIN: "Superadmin",
  ADMIN: "Workspace Admin",
  FULL_ACCESS: "Full Access",
  EDIT_SHARE: "Edit & Share",
  EDIT: "Edit",
};

export function roleDisplayName(role: string | null | undefined): string {
  if (!role) return "";
  return (ROLE_DISPLAY_NAMES as Record<string, string>)[role] || role;
}

/** GET /api/editor/me response — works for any workspace role. */
export type Me = {
  id: string;
  email: string;
  name?: string | null;
  role: WorkspaceRole;
  workspaceAdminId?: string | null;
  workspaceName?: string | null;
  createdAt?: string;
  /** ADMIN only — set when calling /api/admin/me. */
  expiresAt?: string | null;
  /** Billing (Phase 1) — plan + limits + current usage, from /api/editor/me. */
  plan?: string | null;
  planStatus?: string | null;
  /** 'monthly' | 'annual' (null = Free / never paid). Annual = one-time, no autopay. */
  planCycle?: string | null;
  /** Subscription current-period end, or annual expiry date (ISO). */
  planPeriodEnd?: string | null;
  /** Set when the owner cancelled (monthly=at cycle-end, annual=pro-rata refund). ISO. */
  planCanceledAt?: string | null;
  /** Deletion deadline once on Free over-cap (15-day window). ISO. */
  planPurgeAt?: string | null;
  /** 7-day full-access grace end after a failed payment / lapse. ISO. */
  planDunningUntil?: string | null;
  limits?: {
    label: string;
    storageBytes: number;
    members: number;
    projects: number;
    links: number;
    autoDeleteDays: number | null;
    /** Manual share-link expiry: null = adjustable (paid); a number = fixed max days (Free = 5). */
    shareLinkMaxDays?: number | null;
    /** Boolean capability gates — the source of truth is worker/lib/plans.js. */
    features?: {
      restrictedAccess: boolean;
      internalComments: boolean;
      shareControls: boolean;
      shareExpiryControl: boolean;
      customBranding: boolean;
      whiteLabel: boolean;
      authorizedDomains: boolean;
      googleDriveImport: boolean;
    };
  } | null;
  usage?: {
    storageBytes: number;
    projects: number;
    links: number;
    members: number;
  } | null;
  [key: string]: unknown;
};

// ── Auth request/response shapes ─────────────────────────────────────────────

export type WorkspaceLoginRequest = {
  email: string;
  password: string;
};

export type WorkspaceLoginResponse = {
  ok: true;
  mustChangePassword: boolean;
  role: WorkspaceRole;
  /** Full identity — lets the client prime its `me` cache and skip a round-trip. */
  user: Me;
};

export type SuperadminLoginRequest = {
  /** NOTE: superadmin uses `username`, not `email`. */
  username: string;
  password: string;
};

export type SuperadminLoginResponse = { ok: true };

export type RegisterRequest = {
  name: string;
  email: string;
  company?: string;
};

export type ForgotPasswordRequest = {
  email: string;
};

export type ChangePasswordRequest = {
  currentPassword: string;
  newPassword: string;
};

// ── Self-serve auth (email OTP + Google) ─────────────────────────────────────

export type RequestCodeRequest = { email: string };
export type RequestCodeResponse = { ok: true };

export type VerifyCodeRequest = { email: string; code: string };
/**
 * verify-code has two shapes:
 *   - new email    → { ok, existing:false, signupToken }  (go set a password)
 *   - existing user→ { ok, mustChangePassword, role }      (logged in — cookie set)
 * Discriminate on the presence of `signupToken`.
 */
export type VerifyCodeResponse =
  | { ok: true; existing: false; signupToken: string }
  | { ok: true; mustChangePassword: boolean; role: WorkspaceRole; user: Me };

export type CompleteSignupRequest = {
  signupToken: string;
  password: string;
  name?: string;
};
export type CompleteSignupResponse = {
  ok: true;
  mustChangePassword: boolean;
  role: WorkspaceRole;
  user: Me;
};

export type GoogleAuthRequest = { credential: string };
export type GoogleAuthResponse = {
  ok: true;
  mustChangePassword: boolean;
  role: WorkspaceRole;
  user: Me;
};

// ── Domain resources (loose — refined as Tasks 8+ exercise real endpoints) ───

export type Workspace = {
  id: string;
  name: string;
  ownerId: string;
  defaultRetentionDays?: number;
  storageBytes?: number;
  createdAt?: string;
  [key: string]: unknown;
};

export type Project = {
  id: string;
  name: string;
  workspaceId?: string;
  retentionDays?: number | null;
  createdAt?: string;
  /**
   * Display-only marker (locked 2026-07-12). Non-null timestamp = the project
   * has been marked Inactive; NULL / absent = active. Purely informational —
   * uploads/edits/comments/shares still work regardless of this flag. Flip via
   * PATCH /api/editor/projects/:id/status.
   */
  inactiveAt?: string | null;
  /**
   * Read-time aggregates surfaced by GET /api/editor/projects (locked
   * 2026-07-12). Not columns; recomputed on every request from the videos
   * table so there's no denormalized state to drift.
   *
   *   storageBytes — SUM of non-trashed video sizes in the project (0 if empty).
   *   updatedAt    — max(project.createdAt, latest video createdAt).
   *                  A "last activity" heuristic used by the Date Updated sort.
   */
  storageBytes?: number;
  updatedAt?: string;
  /**
   * Cover image — locked 2026-07-13, migration 0010. R2 key + byte count
   * live on the projects row; `coverImageUrl` is derived server-side via
   * getCdnUrl and included on both GET /projects and GET /projects/:id.
   * All three are null when no cover is set. `coverImageBytes` is folded
   * into `storageBytes` at read time — covers count against the quota.
   * Swap or clear via PATCH /projects/:id { coverImageKey, coverImageBytes }
   * — the previous R2 blob is deleted permanently in either path.
   */
  coverImageKey?: string | null;
  coverImageBytes?: number | null;
  coverImageUrl?: string | null;
  /**
   * Restriction (migration 0011, 2026-07-14).
   *   restricted        — true when the project is gated. ADMIN + FULL_ACCESS
   *                       bypass the gate; EDIT / EDIT_SHARE need to appear in
   *                       project_editors (via POST /projects/:id/members).
   *   isProjectMember   — computed server-side per caller. ADMIN + FULL_ACCESS
   *                       always true. For EDIT / EDIT_SHARE, true only when
   *                       they have a project_editors row. Card + navigation
   *                       code gate on `restricted && !isProjectMember`.
   */
  restricted?: boolean;
  isProjectMember?: boolean;
  /** Onboarding sample "Start here" project (migration 0032). 1 = sample. */
  isSample?: number | boolean;
  [key: string]: unknown;
};

/**
 * GET /api/editor/projects/:id/members — one row per invited member. ADMIN +
 * FULL_ACCESS only endpoint. Members always inherit their workspace role —
 * no per-project role, see the "Inherit workspace role" decision 2026-07-14.
 */
export type ProjectMember = {
  userId: string;
  email: string;
  name: string | null;
  role: WorkspaceRole;
  addedAt?: string;
};

/** Matches worker/lib/versions.js / videos.approvalStatus column exactly. */
export type ApprovalStatus = "in_review" | "changes_requested" | "approved";

// UI-only rename locked 2026-07-10 (Task 15 spec). Backend column stays
// `changes_requested`; the label reads as "In Progress" per user preference.
export const APPROVAL_STATUS_LABELS: Record<ApprovalStatus, string> = {
  in_review: "Needs Review",
  changes_requested: "In Progress",
  approved: "Approved",
};

/**
 * Shape returned by GET /api/editor/projects/:id/videos (root videos only,
 * enriched with version-stack info — see enrichRootsWithVersionInfo in
 * worker/routes/editor.js). "title" not "name" — matches the DB column.
 */
export type Video = {
  id: string;
  title: string;
  projectId: string;
  editorId?: string;
  adminId?: string;
  videoRef?: string | null;
  assetType?: "video" | "audio" | "image";
  shareToken?: string | null;
  downloadAllowed?: boolean;
  editorCanToggleDownload?: boolean;
  fileSizeBytes?: number;
  /**
   * Migration 0014 (2026-07-20). Real seconds, captured client-side at upload
   * via HTMLMediaElement.duration. NULL for images and for any pre-0014 row
   * where the client didn't send it. Frontend renders a duration chip on the
   * card only when this is a positive finite number and assetType is
   * video / audio.
   */
  durationSeconds?: number | null;
  expiresAt?: string | null;
  approvalStatus?: ApprovalStatus;
  parentVideoId?: string | null;
  versionNumber?: number;
  createdAt?: string;
  uploaderName?: string | null;
  /** Added by enrichRootsWithVersionInfo — CDN url of the latest version in the stack. */
  cdnUrl?: string | null;
  versionCount?: number;
  latestVersionId?: string;
  latestVersionNumber?: number;
  latestVersionAssetType?: "video" | "audio" | "image";
  /**
   * Stack-wide comment total (root + all versions, includes replies). Set by
   * enrichRootsWithVersionInfo — matches Frame.io behavior where the badge on
   * the card counts every comment across the version stack. 0 when there are
   * no comments; frontend hides the chip in that case.
   */
  commentCount?: number;
  /** Fields v1 (migration 0022) — select-type field chips rendered on the card. */
  fields?: VideoFieldChip[];
  /** Unified Status field (migration 0023) — resolved status option for the card chip. */
  status?: StatusOption | null;
  /** Link review (migration 0024) — provider of the LATEST version in the stack; 'upload' for native files. Set by enrichRootsWithVersionInfo. */
  sourceType?: "upload" | "youtube" | "vimeo";
  /** Link review (0024) — vendor thumbnail URL for link assets (YT/Vimeo CDN); null for uploads / missing. */
  thumbnailUrl?: string | null;
  [key: string]: unknown;
};

/** An option in the extensible Status field (migration 0023). */
export type StatusOption = {
  id: string;
  label: string;
  color: string;
  coreKey: "in_review" | "changes_requested" | "approved" | null;
};

/** A select-type custom-field value shown as a chip on an asset card. */
export type VideoFieldChip = {
  fieldDefId: string;
  label: string;
  value: string;
  color: string | null;
};

/**
 * Folders (migration 0012, 2026-07-14). Unlimited nesting via parentFolderId.
 * Restricted folders enforce their OWN ACL independent of project restriction —
 * a user needing access to a restricted folder inside a restricted project needs
 * BOTH rows.
 *
 * Shape returned by GET /api/editor/projects/:pid/folders and inside the
 * subfolders[] of GET /api/editor/folders/:id/contents. `canEnter=false` +
 * `restrictedAncestorId` set = show the lock icon.
 */
export type Folder = {
  id: string;
  name: string;
  projectId?: string;
  parentFolderId: string | null;
  restricted: boolean;
  /** Direct-child count (subfolders + non-trashed root videos). Does NOT recurse. */
  itemCount?: number;
  canEnter: boolean;
  /** Set when canEnter=false — the restricted ancestor blocking entry. */
  restrictedAncestorId: string | null;
  createdAt?: string | null;
};

/**
 * GET /api/editor/folders/:id — meta + breadcrumb + folder-membership flag.
 */
export type FolderDetail = {
  id: string;
  name: string;
  projectId: string;
  parentFolderId: string | null;
  restricted: boolean;
  isFolderMember: boolean;
  breadcrumb: { id: string; name: string }[];
};

/**
 * GET /api/editor/folders/:id/contents — one round-trip payload combining
 * subfolders + videos for the folder-detail page. `folder` mirrors FolderDetail
 * minus the isFolderMember field (route already gated access before returning).
 */
export type FolderContents = {
  folder: {
    id: string;
    name: string;
    projectId: string;
    parentFolderId: string | null;
    restricted: boolean;
    breadcrumb: { id: string; name: string }[];
  };
  subfolders: Folder[];
  videos: Video[];
};

/** GET /api/editor/folders/:id/members — mirrors ProjectMember. */
export type FolderMember = {
  userId: string;
  email: string;
  name: string | null;
  role: WorkspaceRole;
  addedAt?: string;
};

export type Comment = {
  id: string;
  videoId: string;
  authorId?: string;
  authorName?: string;
  body: string;
  timestamp?: number;
  createdAt?: string;
  [key: string]: unknown;
};

/**
 * GET /api/editor/trash — unified trash list. Each row carries `kind` so the
 * frontend knows whether to call the video or project restore/permanent endpoints.
 * Videos: EDIT sees own, FULL_ACCESS+ sees all. Projects: ADMIN + FULL_ACCESS only.
 * Sorted deletedAt DESC (most-recently-trashed first) by the backend.
 */
export type TrashVideo = {
  kind: "video";
  id: string;
  title: string;
  projectId: string;
  editorId?: string;
  fileSizeBytes?: number;
  videoRef?: string | null;
  assetType?: "video" | "audio" | "image";
  approvalStatus?: ApprovalStatus;
  versionNumber?: number;
  parentVideoId?: string | null;
  deletedAt: string;
  trashExpiresAt: string | null;
  deletionType: "manual_trash" | "cascade_from_project" | null;
  createdAt?: string;
  uploaderName?: string | null;
  projectName?: string | null;
  cdnUrl?: string | null;
  daysLeft: number | null;
};

export type TrashProject = {
  kind: "project";
  id: string;
  name: string;
  retentionDays?: number | null;
  deletedAt: string;
  trashExpiresAt: string | null;
  deletionType: "manual_trash" | null;
  createdAt?: string;
  daysLeft: number | null;
};

export type TrashItem = TrashVideo | TrashProject;

// ── Superadmin ───────────────────────────────────────────────────────────────
// Endpoints under /api/superadmin/*. Auth cookie is shared with workspace users
// but the JWT payload carries role: 'SUPERADMIN'. See [[project-shared-cookie]].

/**
 * Row from GET /api/superadmin/admins — one row per ADMIN user in the users
 * table with live storage aggregates joined in server-side.
 *
 *   currentStorageBytes — SUM(videos.fileSizeBytes) — active + trash — for
 *                         videos owned by this admin. Computed at read time,
 *                         no denormalized column.
 *   peakLast30dBytes    — MAX(MAX(storage_snapshots.totalBytes over last 30d),
 *                         currentStorageBytes). Floor-clamp handles the case
 *                         where today's live total already exceeds every
 *                         snapshot in the window (2am UTC cron hasn't rolled
 *                         up yet). This IS the billing metric — see
 *                         [[project-security-decisions]] peak-last-30d.
 *   peakDate            — YYYY-MM-DD of the snapshot that hit the peak, OR
 *                         today's date if the floor-clamp fired. NULL if the
 *                         workspace has no snapshots yet (brand new admin).
 *   windowStart / windowEnd — YYYY-MM-DD bounds of the 30-day window the
 *                         backend used for the peak computation. Useful if
 *                         the UI wants to display "peak on Aug 3 (in 30d
 *                         window Aug 1 → Aug 30)".
 *   expiresAt           — 40-day rolling account expiry. Past this, workspace
 *                         is locked and share links stop resolving.
 */
export type SuperadminAdmin = {
  id: string;
  name: string;
  email: string;
  expiresAt?: string | null;
  lastLoginAt?: string | null;
  currentStorageBytes: number;
  peakLast30dBytes: number;
  peakDate?: string | null;
  windowStart?: string;
  windowEnd?: string;
  createdAt?: string | null;
  plan?: string | null;
  planStatus?: string | null;
  planPeriodEnd?: string | null;
};

/** Row from GET /api/superadmin/requests — PENDING admin sign-up requests. */
export type SuperadminAdminRequest = {
  id: string;
  name: string;
  email: string;
  company?: string | null;
  status: "PENDING" | "APPROVED" | "REJECTED";
  createdAt?: string | null;
};

export type BillingInvoice = {
  id: string;
  kind: "subscription" | "order";
  planKey?: string | null;
  amountPaise: number;
  currency: string;
  status: string;
  invoiceUrl?: string | null;
  createdAt?: string | null;
};
